/**
 * OrientacionLoading — "Cargando…" de la reserva de la Orientación gratuita
 * (CEC-260928-7b98).
 *
 * Se muestra mientras se descarga el código de la reserva y mientras llega la
 * terapia. Es deliberadamente liviano (sin `Modal`, sin framer-motion): va en
 * el paquete principal de todas las páginas, así que no debe arrastrar el
 * modal ni el wizard. Imita el panel del `Modal` (mismos tokens) y copia su
 * comportamiento: foco dentro de la ventana (no tiene elementos enfocables,
 * así que se enfoca la ventana y Tab no sale), scroll de la página bloqueado,
 * Esc o el fondo cierran, y al desmontarse devuelve el foco y el scroll.
 */
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

export const ORIENTACION_TITULO = 'Reservar Orientación inicial gratuita';

export interface OrientacionLoadingProps {
  onClose: () => void;
}

export function OrientacionLoading({ onClose }: OrientacionLoadingProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Ref-latch como en `Modal`: el effect no se re-ejecuta si `onClose` llega
  // como función inline, así su cleanup (foco y scroll) corre solo al cerrar.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Se monta solo mientras está abierto: montar = abrir, desmontar = cerrar
  // (por Esc, por el fondo, al pasar al wizard o al ir a /terapias).
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, []);

  // Sin elementos enfocables dentro: Tab y Shift+Tab se quedan en la ventana.
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Tab') event.preventDefault();
  }

  return createPortal(
    <div className="fixed inset-0 z-modal" role="presentation">
      <div
        className="absolute inset-0 bg-primary-900/50"
        aria-hidden="true"
        onClick={() => onCloseRef.current()}
      />
      <div className="relative flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={ORIENTACION_TITULO}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className="relative w-full max-w-[480px] rounded-lg bg-cream-100 p-6 text-primary-900 shadow-elev3"
        >
          <h2 className="font-display text-h3 text-primary-900">{ORIENTACION_TITULO}</h2>
          <p role="status" className="mt-4 text-body text-primary-900/70">
            Cargando…
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
